import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, Building2, Linkedin, Pencil, Trash2,
  StickyNote, Clock, Pin,
} from 'lucide-react';
import { useContact, useUpdateContact, useDeleteContact } from '@/hooks/useContacts';
import { useNotes, useCreateNote, useDeleteNote, useToggleNotePin } from '@/hooks/useNotes';
import { useActivities } from '@/hooks/useActivities';
import {
  Button,
  Card,
  Tabs,
  Modal,
  ConfirmDialog,
  Input,
  Select,
  Textarea,
  Skeleton,
} from '@solid/ui';
import { Badge } from '@/components/ui/Badge';
import { CustomFieldsDisplay } from '@/components/CustomFieldsDisplay';
import { CustomFieldsForm } from '@/components/CustomFieldsForm';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import type { ContactStatus } from '@/types';

const statusVariant: Record<ContactStatus, 'default' | 'info' | 'success' | 'error'> = {
  LEAD: 'default',
  QUALIFIED: 'info',
  CUSTOMER: 'success',
  CHURNED: 'error',
};

export default function ContactDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: contact, isLoading } = useContact(id);
  const [tab, setTab] = useState('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);

  const deleteContact = useDeleteContact();

  const handleDelete = async () => {
    if (!id) return;
    await deleteContact.mutateAsync(id);
    navigate('/admin/contacts');
  };

  if (isLoading) {
    return (
      <div className="page-enter">
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-40 rounded-xl" />
        </div>
      </div>
    );
  }

  if (!contact) return null;

  return (
    <div className="page-enter">
      <div className="mb-6">
        <Link to="/admin/contacts" className="inline-flex items-center gap-1 text-sm text-grey-500 hover:text-secondary mb-4">
          <ArrowLeft className="w-4 h-4" />
          Back to Contacts
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-heading-1 text-secondary">
              {contact.firstName} {contact.lastName}
            </h1>
            <div className="flex items-center gap-3 mt-2">
              {contact.jobTitle && (
                <span className="text-body-sm text-grey-500">{contact.jobTitle}</span>
              )}
              <Badge variant={statusVariant[contact.status]}>{contact.status}</Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setShowEdit(true)}>
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setShowDelete(true)} aria-label="Delete contact">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="space-y-4">
          <Card padding="md">
            <h3 className="section-header mb-4">Contact Info</h3>
            <div className="space-y-3">
              {contact.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4 text-grey-400" />
                  <a href={`mailto:${contact.email}`} className="link">{contact.email}</a>
                </div>
              )}
              {contact.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="w-4 h-4 text-grey-400" />
                  <span className="text-grey-600">{contact.phone}</span>
                </div>
              )}
              {contact.linkedinUrl && (
                <div className="flex items-center gap-2 text-sm">
                  <Linkedin className="w-4 h-4 text-grey-400" />
                  <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" className="link">
                    LinkedIn
                  </a>
                </div>
              )}
              {contact.organization && (
                <div className="flex items-center gap-2 text-sm">
                  <Building2 className="w-4 h-4 text-grey-400" />
                  <Link to={`/admin/organizations/${contact.organization.id}`} className="link">
                    {contact.organization.name}
                  </Link>
                </div>
              )}
            </div>
          </Card>

          <Card padding="md">
            <h3 className="section-header mb-2">Source</h3>
            <p className="text-body-sm text-grey-500">{contact.source}</p>
            <h3 className="section-header mb-2 mt-4">Created</h3>
            <p className="text-body-sm text-grey-500">{formatDate(contact.createdAt)}</p>
          </Card>

          <Card padding="md">
            <h3 className="section-header mb-3">Custom Fields</h3>
            <CustomFieldsDisplay entityType="CONTACT" customFields={contact.customFields} />
          </Card>
        </div>

        {/* Main content */}
        <div className="lg:col-span-2">
          <Tabs
            value={tab}
            onValueChange={setTab}
            items={[
              {
                value: 'overview',
                label: 'Overview',
                content: (
                  <Card padding="md">
                    <h3 className="section-header mb-4">Deals</h3>
                    <p className="section-subheader">Deals associated with this contact will appear here.</p>
                  </Card>
                ),
              },
              { value: 'activity', label: 'Activity', content: <ActivityTimeline contactId={id!} /> },
              { value: 'notes', label: 'Notes', content: <NotesSection contactId={id!} /> },
            ]}
          />
        </div>
      </div>

      {showEdit && (
        <EditContactDialog
          contact={contact}
          open={showEdit}
          onOpenChange={setShowEdit}
        />
      )}

      <ConfirmDialog
        open={showDelete}
        onOpenChange={setShowDelete}
        title="Delete contact?"
        description="Are you sure you want to delete this contact?"
        confirmLabel="Delete"
        destructive
        onConfirm={handleDelete}
      />
    </div>
  );
}

function ActivityTimeline({ contactId }: { contactId: string }) {
  const { data: activities, isLoading } = useActivities({ contactId });

  if (isLoading) return <Skeleton className="h-32 rounded-xl" />;

  if (!activities?.length) {
    return (
      <Card padding="md">
        <div className="text-center py-8">
          <Clock className="w-8 h-8 text-grey-300 mx-auto mb-2" />
          <p className="text-body-sm text-grey-500">No activity yet.</p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {activities.map((activity) => (
        <Card key={activity.id} padding="sm">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-secondary">
                {activity.type.replace(/_/g, ' ')}
              </p>
              {activity.actor && (
                <p className="text-xs text-grey-500">by {activity.actor.name}</p>
              )}
            </div>
            <span className="text-xs text-grey-400">{formatRelativeTime(activity.createdAt)}</span>
          </div>
        </Card>
      ))}
    </div>
  );
}

function NotesSection({ contactId }: { contactId: string }) {
  const { data: notes, isLoading } = useNotes({ contactId });
  const createNote = useCreateNote();
  const deleteNote = useDeleteNote();
  const togglePin = useToggleNotePin();
  const [newNote, setNewNote] = useState('');

  const handleCreate = async () => {
    if (!newNote.trim()) return;
    await createNote.mutateAsync({ content: newNote, contactId });
    setNewNote('');
  };

  return (
    <div className="space-y-4">
      <Card padding="md">
        <Textarea
          placeholder="Add a note..."
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          className="mb-3"
        />
        <div className="flex justify-end">
          <Button size="sm" onClick={handleCreate} loading={createNote.isPending} disabled={!newNote.trim()}>
            <StickyNote className="w-3.5 h-3.5" />
            Add Note
          </Button>
        </div>
      </Card>

      {isLoading ? (
        <Skeleton className="h-32 rounded-xl" />
      ) : !notes?.length ? (
        <Card padding="md">
          <div className="text-center py-8">
            <StickyNote className="w-8 h-8 text-grey-300 mx-auto mb-2" />
            <p className="text-body-sm text-grey-500">No notes yet.</p>
          </div>
        </Card>
      ) : (
        notes.map((note) => (
          <Card key={note.id} padding="sm">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-secondary">{note.author.name}</span>
                <span className="text-xs text-grey-400">{formatRelativeTime(note.createdAt)}</span>
                {note.isPinned && <Pin className="w-3 h-3 text-primary" />}
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => togglePin.mutate(note.id)}
                  className="p-1 rounded hover:bg-grey-100 text-grey-400 hover:text-grey-600"
                  aria-label={note.isPinned ? 'Unpin note' : 'Pin note'}
                >
                  <Pin className="w-3.5 h-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => deleteNote.mutate(note.id)}
                  className="p-1 rounded hover:bg-grey-100 text-grey-400 hover:text-error"
                  aria-label="Delete note"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <div className="text-sm text-grey-600 whitespace-pre-wrap">{note.content}</div>
          </Card>
        ))
      )}
    </div>
  );
}

function EditContactDialog({
  contact,
  open,
  onOpenChange,
}: {
  contact: NonNullable<ReturnType<typeof useContact>['data']>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [form, setForm] = useState({
    firstName: contact.firstName,
    lastName: contact.lastName,
    email: contact.email || '',
    phone: contact.phone || '',
    jobTitle: contact.jobTitle || '',
    status: contact.status,
  });
  const [customFields, setCustomFields] = useState<Record<string, any>>(contact.customFields || {});
  const updateContact = useUpdateContact();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await updateContact.mutateAsync({
      id: contact.id,
      data: {
        ...form,
        email: form.email || undefined,
        phone: form.phone || undefined,
        jobTitle: form.jobTitle || undefined,
        customFields,
      },
    });
    onOpenChange(false);
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Edit Contact"
      footer={
        <>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="edit-contact-form" loading={updateContact.isPending}>
            Save Changes
          </Button>
        </>
      }
    >
      <form id="edit-contact-form" onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="First Name"
            required
            value={form.firstName}
            onChange={(e) => setForm({ ...form, firstName: e.target.value })}
          />
          <Input
            label="Last Name"
            required
            value={form.lastName}
            onChange={(e) => setForm({ ...form, lastName: e.target.value })}
          />
        </div>
        <Input
          label="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        <Input
          label="Phone"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
        <Input
          label="Job Title"
          value={form.jobTitle}
          onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
        />
        <Select
          label="Status"
          value={form.status}
          onChange={(e) => setForm({ ...form, status: e.target.value as any })}
          options={[
            { value: 'LEAD', label: 'Lead' },
            { value: 'QUALIFIED', label: 'Qualified' },
            { value: 'CUSTOMER', label: 'Customer' },
            { value: 'CHURNED', label: 'Churned' },
          ]}
        />
        <CustomFieldsForm
          entityType="CONTACT"
          currentValues={customFields}
          onChange={setCustomFields}
        />
      </form>
    </Modal>
  );
}
